import { useDispatch, useSelector } from "react-redux";

import { setSelectedShapefile } from "store/features/project/projectSlice";
import { RootState } from "store/rootReducer";

import { useStructuredNodeContext } from "components/project/layers/components/layer-managers/project-layer-tree/node-manager/context/StructuredNodeContext";
import {
  acceptNodeChanges,
  cancelNodeChanges
} from "components/project/layers/components/layer-managers/project-layer-tree/node-manager/context/reducer";
import { ProjectTreeNodeT } from "components/project/layers/hooks";
import { useProjectDispatch } from "components/project/projects/context";
import { useUpdateProjectShapefileMutation } from "components/project/shapefiles/mutations";

export type UseAcceptShapefileChangesParams = {
  node: ProjectTreeNodeT;
  projectId: string;
};

export type UseAcceptShapefileChangesReturn = {
  mutate: (e?: React.SyntheticEvent) => void;
  isLoading: boolean;
};

export function useAcceptShapefileChanges(
  params: UseAcceptShapefileChangesParams
): UseAcceptShapefileChangesReturn {
  const { node, projectId } = params;

  const projectDispatch = useProjectDispatch();
  const [{ editableTitle }, nodeDispatch] = useStructuredNodeContext();
  const dispatch = useDispatch();
  const selectedShapefile = useSelector(
    (state: RootState) => state.project.selectedShapefile
  );

  const updateShapefile = useUpdateProjectShapefileMutation({
    projectId,
    onSuccess: () => {
      if (
        selectedShapefile &&
        (selectedShapefile.shapefileId === node.shapefileId ||
          selectedShapefile.projectShapefileId === node.shapefileId)
      ) {
        dispatch(setSelectedShapefile({ ...selectedShapefile, title: editableTitle }));
      }
      acceptNodeChanges(nodeDispatch, (isDraggable) =>
        projectDispatch({ payload: { isTreeDraggable: isDraggable } })
      );
    }
  });

  const mutate = (e?: React.SyntheticEvent) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();

    if (node.title === editableTitle) {
      cancelNodeChanges(nodeDispatch, (isDraggable) =>
        projectDispatch({ payload: { isTreeDraggable: isDraggable } })
      );
      return;
    }

    updateShapefile.mutate({
      body: { key: "name", value: editableTitle },
      projectId,
      shapefileId: node.shapefileId
    });
  };

  return { mutate, isLoading: updateShapefile.isLoading };
}
